/**
 * Whether a turn has a chat model it can call: the rule, written once.
 *
 * The provider's local configuration readiness can withhold a registered model:
 * saving its row does not mean its required credential is present. This is not
 * a service connection test. The other facts are the default the
 * owner chose (stored where the model check's `readiness.default_namespace`
 * and `default_field` say) and the models registered right now (the `models`
 * registry). A chosen default has to be registered. With nothing chosen, the
 * model its plugin registered as standing in is the one a turn calls
 * (`metadata.stands_in`: for `llm_providers`, the first in its list, as
 * `service.py` `_default_key` resolves it). The plugin says which, because it
 * resolves the model on every turn; this page never works it out again.
 *
 * `ModelReadiness` draws the answer on an Agent's own pages; the Hub waits on
 * it before taking an owner into an Agent it has just created. Neither decides
 * for itself, so the two cannot disagree.
 */

import type { CapabilityEntry } from "../lib/api";
import type { SettingsDestination } from "./directory";

export interface ModelSettlement {
  /** The default chosen where it is stored, or "" when none is. */
  current: string;
  /** The registered model the chosen default names, if it is registered. */
  chosen?: CapabilityEntry;
  /** Nothing chosen, and the one model its plugin registered as standing in. */
  standingIn?: CapabilityEntry;
  /** A turn has a model to call: the chosen one, or the one standing in. */
  settled: boolean;
  /** A provider's local configuration problem; no remote request was made. */
  reason: string;
}

export function modelSettlement(
  /** The stored values of the namespace holding the default. */
  values: Record<string, unknown>,
  /** The live `models` registry. */
  registered: readonly CapabilityEntry[],
  /** Which field holds the default. */
  check: Pick<DeclaredModelCheck, "defaultField">,
): ModelSettlement {
  const stored = values[check.defaultField];
  const current = typeof stored === "string" ? stored : "";
  const chosen = registered.find(model => model.key === current);
  // Two plugins each saying theirs stands in is no answer.
  const marked = registered.filter(model => (model.spec?.metadata as { stands_in?: unknown } | undefined)?.stands_in === true);
  const standingIn = !current && marked.length === 1 ? marked[0] : undefined;
  const selected = chosen ?? standingIn;
  const readiness = (selected?.spec?.metadata as { readiness?: { ready: boolean; reason: string } } | undefined)?.readiness;
  return { current, chosen, standingIn, settled: selected !== undefined && readiness?.ready !== false,
    reason: readiness?.ready === false ? readiness.reason : "" };
}

/** The settings page that declared the model check, and what the rule needs from the declarations. */
export interface DeclaredModelCheck {
  /** The page that declared `readiness`: where the models are connected. */
  namespace: string;
  /** Where the chosen default is stored, as that page declared it. */
  defaultNamespace: string;
  /**
   * The field there that holds it. The check is where the default is chosen,
   * so the form of `defaultNamespace` leaves this field out (`ConnectionsPage`).
   */
  defaultField: string;
  /** The field there that holds what runs when the chosen model fails, if the plugin said where. */
  fallbackField?: string;
  /**
   * The choice field there that holds the owner's level for the models somebody else supplies, if the plugin
   * said where. Like `defaultField` it is set where the model is chosen, so the form leaves it out.
   */
  suppliedLevelField?: string;
}

/**
 * The model check as an Agent's own settings declare it, or undefined while
 * they do not say enough to apply the rule.
 *
 * The page holding the default has to be there too: the default is read from
 * it, and until it is there an unread default would pass for an empty one.
 */
export function declaredModelCheck(destinations: SettingsDestination[]): DeclaredModelCheck | undefined {
  const check = destinations.find(item => item.namespace && item.declared.readiness?.default_namespace);
  const defaultNamespace = check?.declared.readiness?.default_namespace;
  const defaultField = check?.declared.readiness?.default_field;
  const holder = destinations.find(item => item.namespace && item.namespace === defaultNamespace);
  if (!check?.namespace || !defaultNamespace || !defaultField || !holder) return undefined;
  return {
    namespace: check.namespace, defaultNamespace, defaultField,
    fallbackField: check.declared.readiness?.fallback_field,
    suppliedLevelField: check.declared.readiness?.supplied_level_field,
  };
}
